import {
  ADDON_PRODUCT_ANALYTICS_FREE_EVENTS,
  ADDON_PRODUCT_ANALYTICS_PRICE_PER_EVENT,
  ADDON_PRODUCT_STORAGE_TYPES,
} from "@stores/addons/constants";
import { offerProductAnalytics } from "@stores/config/utils";
import { METER_PRODUCT_ANALYTICS_EVENTS_KEY } from "@stores/subscriptions/constants";
import { getPayAsYouGoMeterValues } from "@stores/subscriptions/utils";
import { ManageCard } from "./ManageCard";
import { formatPrice, formatQuantity } from "@common/utils/string";
import { hasStorage, meteredLineItem } from "../utils";
import {
  anyOf,
  IAddOnContext,
  IUpsertContext,
  IMeteredAddOnDescriptor,
} from "../types";

// harbormaster reports 0 on both rows when the allowance only lives in Stripe
const getIncludedUnits = ({ subscribed, addOn }: IAddOnContext): number =>
  subscribed?.free_units ||
  addOn?.free_units ||
  ADDON_PRODUCT_ANALYTICS_FREE_EVENTS;

/** Paid Metabase Track. */
const getPricePerUnit = ({ subscribed, addOn }: IAddOnContext): number =>
  (subscribed?.price_per_unit ?? addOn?.default_price_per_unit) ||
  ADDON_PRODUCT_ANALYTICS_PRICE_PER_EVENT;

const productAnalytics = {
  productType: "product-analytics",
  kind: "metered",
  title: "Metabase Track",
  isVisible: ({
    config,
    subscribed,
    subscribedByType,
    isCloudHosted,
  }: IAddOnContext) =>
    isCloudHosted &&
    !subscribedByType["product-analytics-free"] &&
    // stays visible once enabled, even after the offer flag is turned off
    (!!subscribed || offerProductAnalytics(config)),

  requires: anyOf(ADDON_PRODUCT_STORAGE_TYPES),

  // must stay gated on the subscription, not on the meter: both Track variants
  // report under the same meter key, so the key alone cannot say whose it is
  getUsage: (ctx: IAddOnContext) => {
    if (!ctx.subscribed) {
      return undefined;
    }

    const meter = ctx.purchase.meters?.[METER_PRODUCT_ANALYTICS_EVENTS_KEY];
    const usage = meter?.meter_value ?? 0;
    const includedUnits = getIncludedUnits(ctx);

    return {
      hasQuota: typeof meter?.meter_value === "number",
      usage,
      includedUnits,
      ...getPayAsYouGoMeterValues({
        meterValue: meter?.meter_value,
        freeUnits: includedUnits,
        meterFreeUnits: meter?.meter_free_units,
      }),
    };
  },

  getIncludedUnits,

  getPricePerUnit,
  // An account with no warehouse gets the usage-based one bundled in, asked for
  // by product type — so storage never learns that Track exists.
  getUpsertAddOns: (ctx: IUpsertContext) => [
    ...(hasStorage(ctx) ? [] : ctx.getUpsertAddOnsFor("dwh-rent")),
    { product_type: "product-analytics", prepaid_units: 0 },
  ],

  getEstimateRowItem: (ctx) => ({
    detail: "Usage-based billing",
    price:
      ctx.addOn &&
      `${formatPrice(1_000_000 * getPricePerUnit(ctx))}/1M events (after first ${formatQuantity(getIncludedUnits(ctx))} events)`,
  }),

  getBillingRowItem: () => ({ detail: "Usage-based billing" }),
  getPurchaseLineItem: (ctx) => meteredLineItem(ctx, "ingested events"),

  ManageCard,
} satisfies IMeteredAddOnDescriptor;

export default productAnalytics;
